import React, { forwardRef, memo } from 'react'
import { Typography, Skeleton, styled, Box } from '@mui/material'
import { RawEventMessage } from '@weblite/ts-action/message'
import { TextParser } from '@weblite/web-textparser'
import { getUserDisplayName, useUser } from '$logic/users'
import { openUserProfile } from '$logic/router'
import { t } from '$i18n'
import { scrollToMessageByIid, useOpenedRoomId } from '$logic/opened-room'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { getPinnedMessageDecorator } from '$logic/pin'
import { getMessageByIid } from '$logic/message'
import { showMessagesNotExistSnackbar } from '$comp/snackbars'
import { hyperLinkViewer } from '$common/hyperLink-viewer'
import { EMOJI_SPRITE_SHEET } from '$helper/constants/emoji'
import { useEventTokens } from './react-hook'

const parsers = [
  { name: 'emoji', args: { spritesheeturl: EMOJI_SPRITE_SHEET } },
  { name: 'hyperLink', fn: hyperLinkViewer },
]
const TextParserContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.caption1,
  color: theme.palette.white,
  lineHeight: '16px',
  textAlign: 'initial',
  '--emoji-size': '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  borderBottom: '1px solid',
  cursor: 'pointer',
}))
function MessageDecorator({ messageIid }: { messageIid: number }) {
  const roomId = useOpenedRoomId()
  const { value, loading } = useAsyncFunction(async () => {
    const message = await getMessageByIid(roomId, messageIid)
    return getPinnedMessageDecorator(message)
  }, [messageIid])

  return loading ? (
    <Skeleton variant="text" width="40px" />
  ) : !value ? (
    <Typography
      color="white"
      variant="caption1"
      sx={{ display: 'block', borderBottom: '1px solid', cursor: 'pointer' }}
      onClick={() => showMessagesNotExistSnackbar()}
      noWrap
    >
      {t('chat.aMessage')}
    </Typography>
  ) : (
    <TextParserContainer
      dir="auto"
      onClick={() => scrollToMessageByIid(messageIid)}
    >
      <TextParser text={value} parsers={parsers} />
    </TextParserContainer>
  )
}

function UserDisplayName({ userId }: { userId: string }) {
  const { user } = useUser(userId)
  const displayName = user && getUserDisplayName(user)

  return user ? (
    <Typography
      color="white"
      variant="caption1"
      sx={{ display: 'block', borderBottom: '1px solid', cursor: 'pointer' }}
      onClick={() => openUserProfile(userId)}
      noWrap
    >
      {displayName}
    </Typography>
  ) : (
    <Skeleton variant="text" width="40px" />
  )
}

const Container = styled('div')`
  display: flex;
  align-items: center;

  width: fit-content;
  margin: 0 auto;
  padding: 3px 30px;
  border-radius: 21px;
  background-color: #0000004d;
  text-align: center;
  overflow: hidden;
  min-height: 30px;
`

const TokenContainer = styled('div')<{ isUser: boolean; isMessage: boolean }>`
  flex-shrink: ${({ isUser, isMessage }) => (isUser ? 1 : isMessage ? 2 : 0)};
  overflow: hidden;

  margin: 0 2px;
  &:first-of-type {
    /* @noflip */
    margin-right: 0;
  }
  &:last-child {
    /* @noflip */
    margin-left: 0;
  }
`

export const Event = memo(
  forwardRef(function EventComponent(
    {
      message,
      roomId,
    }: {
      message: RawEventMessage
      roomId: string
    },
    ref: any,
  ) {
    const { eventTokens } = useEventTokens({ message, roomId })

    // TODO: Implement loading
    return (
      <Container ref={ref}>
        {eventTokens ? (
          eventTokens.map(token =>
            token ? (
              <TokenContainer
                isUser={token.type === 'USER'}
                isMessage={token.type === 'MESSAGE'}
              >
                {token.type === 'TEXT' ? (
                  <Typography
                    color="white"
                    variant="caption1"
                    sx={{ flexShrink: 0 }}
                  >
                    {token.text}
                  </Typography>
                ) : token.type === 'MESSAGE' ? (
                  <MessageDecorator messageIid={token.messageIid} />
                ) : (
                  <UserDisplayName userId={token.userId} />
                )}
              </TokenContainer>
            ) : (
              <Typography color="white" variant="caption1">
                ...
              </Typography>
            ),
          )
        ) : (
          <Typography color="white" variant="caption1">
            ...
          </Typography>
        )}
      </Container>
    )
  }),
)
